// imageOptimize — shrinks a picture in the browser BEFORE it is uploaded, so a
// 4 MB phone photo does not become a 4 MB logo that every visitor downloads.
//
//   const small = await optimizeImage(file, "logo");   // a File, ready to upload
//
// What it does: scales the picture down so its longer side is at most the
// maximum for its KIND (a logo needs far fewer pixels than a hero background),
// keeps the camera's orientation, and re-encodes it as WebP (JPEG for the
// social-share picture, which crawlers read less reliably as WebP). Transparency
// is kept for WebP output, so logos stay transparent.
//
// How strong it is = the company's "Image quality" setting (Config → Appearance
// → image_quality): high / balanced (default) / small / original (= do nothing).
// It is read from the cached public config, so it needs no extra request.
//
// It never makes things worse: GIF and SVG are left alone (a GIF may be
// animated), a picture that is already small is left alone, the original is
// uploaded when the result would not be smaller, and any decode / encode
// failure (e.g. HEIC in Chrome) falls back to the original file.
import { notify } from "./notify";

export const QUALITY_LEVELS = {
  // quality = encoder quality (0..1); scale = multiplier on the kind's maximum side
  high: { quality: 0.92, scale: 1.25 },
  balanced: { quality: 0.82, scale: 1 },
  small: { quality: 0.7, scale: 0.75 },
};

// Longest side in pixels at the "balanced" level. `type` forces the output.
export const IMAGE_KINDS = {
  logo: { max: 800 },
  logo_dark: { max: 800 },
  share: { max: 1600, type: "image/jpeg" },
  product: { max: 1600 },
  service: { max: 1600 },
  item: { max: 1600 },
  blog: { max: 1600 },
  avatar: { max: 512 },
  featured: { max: 2000 },
  header_bg: { max: 2400 },
  block: { max: 2400 },
};

const SKIP_BELOW_BYTES = 40 * 1024; // tiny already — re-encoding would only blur it
const MIN_GAIN = 0.95; // result must be at least 5% smaller than the original
const SKIP_TYPES = /^image\/(gif|svg\+xml|x-icon|vnd\.microsoft\.icon)$/i;

export function getImageQualityLevel() {
  try {
    const cached = JSON.parse(localStorage.getItem("systemSettings") || "null");
    const v = String(cached?.appearance?.image_quality || "").toLowerCase();
    if (v === "high" || v === "balanced" || v === "small" || v === "original") return v;
  } catch {
    /* no cached settings — use the default */
  }
  return "balanced";
}

const formatBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function canvasToBlob(canvas, type, quality) {
  if (canvas.convertToBlob) return canvas.convertToBlob({ type, quality }); // OffscreenCanvas
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function makeCanvas(w, h) {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

const swapExt = (name, ext) => `${(name || "image").replace(/\.[^./\\]+$/, "")}${ext}`;

// optimizeImageDetailed — { file, before, after, changed }; never rejects.
export async function optimizeImageDetailed(file, kind = "block", options = {}) {
  const unchanged = { file, before: file?.size || 0, after: file?.size || 0, changed: false };
  try {
    if (!file || !/^image\//i.test(file.type || "") || SKIP_TYPES.test(file.type)) return unchanged;
    const level = options.level || getImageQualityLevel();
    if (level === "original") return unchanged;
    const spec = IMAGE_KINDS[kind] || IMAGE_KINDS.block;
    const preset = QUALITY_LEVELS[level] || QUALITY_LEVELS.balanced;
    const maxSide = Math.round(spec.max * preset.scale);

    if (typeof createImageBitmap !== "function") return unchanged;
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      return unchanged; // not decodable here (e.g. HEIC) — upload the original
    }
    const longSide = Math.max(bitmap.width, bitmap.height);
    const ratio = Math.min(1, maxSide / longSide);
    if (ratio === 1 && file.size < SKIP_BELOW_BYTES) {
      bitmap.close && bitmap.close();
      return unchanged;
    }
    const w = Math.max(1, Math.round(bitmap.width * ratio));
    const h = Math.max(1, Math.round(bitmap.height * ratio));
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext("2d");
    const outType = spec.type || "image/webp";
    if (outType === "image/jpeg") {
      ctx.fillStyle = "#ffffff"; // JPEG has no transparency
      ctx.fillRect(0, 0, w, h);
    }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close && bitmap.close();

    let blob = await canvasToBlob(canvas, outType, preset.quality);
    // A browser that cannot encode WebP silently returns PNG — then try JPEG,
    // but only for pictures without transparency (a PNG/WebP source may have it).
    if (!blob || blob.type !== outType) {
      const mayHaveAlpha = /png|webp/i.test(file.type);
      if (outType !== "image/jpeg" && !mayHaveAlpha) blob = await canvasToBlob(canvas, "image/jpeg", preset.quality);
      if (!blob || (blob.type !== "image/jpeg" && blob.type !== outType)) return unchanged;
    }
    if (blob.size >= file.size * MIN_GAIN && ratio === 1) return unchanged;
    if (blob.size >= file.size) return unchanged;

    const ext = blob.type === "image/jpeg" ? ".jpg" : ".webp";
    const out = new File([blob], swapExt(file.name, ext), { type: blob.type, lastModified: Date.now() });
    return { file: out, before: file.size, after: out.size, changed: true };
  } catch {
    return unchanged;
  }
}

// optimizeImage — the File to upload (the original when nothing was gained).
// A short toast tells the user when a big picture was shrunk noticeably.
export async function optimizeImage(file, kind = "block", options = {}) {
  const r = await optimizeImageDetailed(file, kind, options);
  if (r.changed && r.before - r.after >= 300 * 1024 && options.notify !== false) {
    try {
      notify.info(`Image optimised: ${formatBytes(r.before)} → ${formatBytes(r.after)}`);
    } catch {
      /* a toast must never block an upload */
    }
  }
  return r.file;
}
